/**
 * Реактивное состояние «коллапса по скроллу» для sticky-формы поиска.
 *
 * Сценарий (app-слой компонует шапку + форму, без их связки между собой —
 * Req 11.3): при скролле вниз шапка уходит за верх экрана и плавно гаснет, а
 * форма поиска прилипает к верху. При возврате в самый верх (scrollY → 0) шапка
 * снова появляется, форма отлипает. Это **позиционная** модель (а не «умная
 * шапка» по направлению скролла): состояние зависит только от текущего scrollY.
 *
 * Сам «прилипон» формы делает CSS (`position: sticky; top: 0` на её обёртке) —
 * нативно, без скачков потока и без spacer'а. Этот composable отвечает лишь за
 * **косметику анимации** и не трогает позиционирование:
 *  - `progress` (0..1, привязан к scrollY) — для fade шапки, синхронного со
 *    скроллом: `:style="{ opacity: 1 - progress }"`. Так fade идёт ровно пока
 *    шапка уезжает за экран — без артефакта «пустой полосы» над прилипшей формой
 *    (который возник бы при time-based transition, гасящем шапку быстрее скролла);
 *  - `collapsed` — форма уже прилипла (scrollY ≥ дистанции), шапка ушла; драйвит
 *    «оторванную» тень формы и, при желании, `aria`-флаги.
 *
 * Дистанция коллапса = высота `target` (обычно шапки): форма прилипает ровно
 * когда шапка ушла за экран. Без `target` берётся `distance` (по умолчанию 1px →
 * коллапс «сразу при первом пикселе скролла», как просили — но тогда fade шапки
 * вырождается в мгновенный; для плавного fade передавайте `target`).
 *
 * SSR-safe: вне браузера возвращает нейтральное состояние и ничего не вешает.
 * Слушатели (scroll/resize) — пассивные и rAF-throttled, снимаются на unmount.
 */

import { onMounted, onUnmounted, ref, unref, watch } from 'vue';
import type { Ref } from 'vue';

/** Опции `useStickyOnScroll()`. */
export interface UseStickyOnScrollOptions {
  /**
   * Дистанция коллапса в пикселях: на ней `progress` достигает 1, а `collapsed`
   * становится `true`. По умолчанию `1`. Если задан `target` (и он измерим),
   * дистанция берётся из его высоты и `distance` игнорируется.
   */
  distance?: number | Ref<number>;
  /**
   * Элемент, по высоте которого считается дистанция коллапса (обычно шапка):
   * форма прилипает к верху ровно когда `target` ушёл за экран. Высота
   * пересчитывается на `resize`/смене ссылки.
   */
  target?: Ref<HTMLElement | null>;
}

/** Публичный контракт `useStickyOnScroll()`. */
export interface UseStickyOnScroll {
  /**
   * Свёрнуто ли: проскроллили за дистанцию коллапса (форма прилипла, шапка ушла).
   * Драйвит «оторванную» тень формы / `aria`-флаги. `false` в самом верху.
   */
  collapsed: Ref<boolean>;
  /**
   * Прогресс коллапса `0..1` = `clamp(scrollY / distance, 0, 1)`. Для fade шапки,
   * привязанного к скроллу: `:style="{ opacity: 1 - progress }"`.
   */
  progress: Ref<number>;
}

/** Дефолтная дистанция, когда нет ни `target`, ни валидного `distance`. */
const FALLBACK_DISTANCE = 1;

/**
 * Создаёт реактивное состояние коллапса по вертикальному скроллу окна.
 */
export function useStickyOnScroll(
  options: UseStickyOnScrollOptions = {},
): UseStickyOnScroll {
  const { distance, target } = options;

  const collapsed = ref(false);
  const progress = ref(0);

  // SSR / не-браузер: нейтральное состояние, без подписок и доступа к window.
  if (typeof window === 'undefined') {
    return { collapsed, progress };
  }

  const raf: (cb: FrameRequestCallback) => number =
    window.requestAnimationFrame?.bind(window) ??
    ((cb) => window.setTimeout(() => cb(0), 16));
  const caf: (id: number) => void =
    window.cancelAnimationFrame?.bind(window) ?? window.clearTimeout.bind(window);

  const readScrollY = (): number =>
    window.scrollY ?? window.pageYOffset ?? document.documentElement?.scrollTop ?? 0;

  const readDistance = (): number => {
    const measured = target?.value?.offsetHeight;
    if (measured && measured > 0) {
      return measured;
    }
    const configured = unref(distance);
    return typeof configured === 'number' && configured > 0
      ? configured
      : FALLBACK_DISTANCE;
  };

  const update = (): void => {
    const d = Math.max(readDistance(), 1);
    const y = readScrollY();
    progress.value = Math.min(Math.max(y / d, 0), 1);
    collapsed.value = y >= d;
  };

  let ticking = false;
  let rafId = 0;
  const requestTick = (): void => {
    if (ticking) {
      return;
    }
    ticking = true;
    rafId = raf(() => {
      ticking = false;
      update();
    });
  };

  onMounted(() => {
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick, { passive: true });
    // Начальное состояние (напр. при восстановлении scroll-позиции на загрузке).
    update();
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', requestTick);
    window.removeEventListener('resize', requestTick);
    if (rafId) {
      caf(rafId);
    }
  });

  // Пересчёт при смене конфигурируемой дистанции, переданной как ref.
  if (distance && typeof distance !== 'number') {
    watch(distance, requestTick);
  }

  return { collapsed, progress };
}
